import React from 'react';
import {
    MeInfoResponse,
    ProviderClaim,
    ProviderComment,
    ProviderMessage,
    SupportClaim,
    SupportComment,
    SupportMessage,
} from '../../api';

async function noop(d?: any): Promise<void> {
    return undefined;
}

async function noopClaimSearch(term: string): Promise<(ProviderClaim | SupportClaim)[]> {
    return [];
}

export interface IComment {
    messageId: string;
    body: string;
    note: boolean;
    files?: File[];
}

export interface IMessage {
    subject: string;
    body: string;
    note: boolean;
    claimId?: string;
    files?: File[];
    priority?: string;
    dueDate?: string;
    links?: {
        claims?: Array<{ id: string; entityType: string }>;
        [key: string]: Array<{ id: string; entityType: string }> | undefined;
    };
}

export interface IDocument {
    id?: string;
    filename?: string;
    description?: string;
    width?: number;
    height?: number;
    filesize?: number;
    notes?: string;
    mimetype?: string;
    thumbnail?: string;
    claimId?: string;
    active?: boolean;
}

export interface IMessagingContextProps {
    isReadOnly?: boolean;
    isSubjectEditable?: boolean;
    isListLoading?: boolean;
    isDetailLoading?: boolean;
    enableMessageTypeValidation: boolean;
    isReadOnlyClaimChoice?: boolean;

    account: MeInfoResponse | undefined;
    message: ProviderMessage | SupportMessage | undefined;
    providerMessage?: ProviderMessage | undefined;
    comments: (ProviderComment | SupportComment)[] | undefined;
    documents: IDocument[] | undefined;
    claims: (ProviderClaim | SupportClaim)[] | undefined;

    getCategoryName?: (categoryId: string) => string | undefined;
    getUserName?: (userId: string) => string | undefined;
    onClaimSearch: (term: string) => Promise<(ProviderClaim | SupportClaim)[]>;
    onCommentToggle: (mid: string, cid: string, note: boolean) => Promise<void>;
    onComposeComment: (comment: IComment) => Promise<void>;
    onComposeMessage: (message: IMessage) => Promise<void>;
    onDocumentDownload: (documentId: string) => Promise<void>;
    onDocumentActualDownload?: (documentId: string) => Promise<string>;
    onDocumentAssign: (document: IDocument) => Promise<void>;
    onDocumentEdit?: (document: IDocument) => Promise<void>;
    onMarkAsClosed: (ids: string | string[]) => Promise<void>;
    onMarkAsOpen: (ids: string | string[]) => Promise<void>;
    onMarkAsRead: (ids: string | string[]) => Promise<void>;
    onMarkAsUnread: (ids: string | string[]) => Promise<void>;
    onProviderMarkAsClosed?: (id: string) => Promise<void>;
    onProviderMarkAsOpen?: (id: string) => Promise<void>;
    onMessageClose: () => Promise<void>;
    onMessageOpen: (id: string) => Promise<void>;
    onSubjectUpdate: (id: string, subject: string) => Promise<void>;
    onClaimUpdate: (id: string, claimId: string | null) => Promise<void>;
    onLinkEntityToMessage?: (id: string, entityType: string, entityIds: string[]) => Promise<void>;
    onUnlinkEntityFromMessage?: (id: string, entityType: string, entityIds: string[]) => Promise<void>;
}

export interface IMessageProviderProps {
    children: React.ReactNode;

    isReadOnly?: boolean;
    isSubjectEditable?: boolean;
    isListLoading?: boolean;
    isDetailLoading?: boolean;
    enableMessageTypeValidation: boolean;
    isReadOnlyClaimChoice?: boolean;

    account: MeInfoResponse | undefined;
    message: ProviderMessage | SupportMessage | undefined;
    providerMessage?: ProviderMessage | undefined;
    comments: (ProviderComment | SupportComment)[] | undefined;
    documents: IDocument[] | undefined;
    claims: (ProviderClaim | SupportClaim)[] | undefined;

    getCategoryName?: (categoryId: string) => string | undefined;
    getUserName?: (userId: string) => string | undefined;
    onClaimSearch: (term: string) => Promise<(ProviderClaim | SupportClaim)[]>;
    onCommentToggle?: (mid: string, cid: string, note: boolean) => Promise<void>;
    onComposeComment?: (comment: IComment) => Promise<void>;
    onComposeMessage?: (message: IMessage) => Promise<void>;
    onDocumentDownload: (documentId: string) => Promise<void>;
    onDocumentActualDownload?: (documentId: string) => Promise<string>;
    onDocumentAssign?: (document: IDocument) => Promise<void>;
    onDocumentEdit?: (document: IDocument) => Promise<void>;
    onMarkAsClosed?: (ids: string | string[]) => Promise<void>;
    onMarkAsOpen?: (ids: string | string[]) => Promise<void>;
    onMarkAsRead?: (ids: string | string[]) => Promise<void>;
    onMarkAsUnread?: (ids: string | string[]) => Promise<void>;
    onProviderMarkAsClosed?: (id: string) => Promise<void>;
    onProviderMarkAsOpen?: (id: string) => Promise<void>;
    onMessageClose: () => Promise<void>;
    onMessageOpen?: (id: string) => Promise<void>;
    onSubjectUpdate?: (id: string, subject: string) => Promise<void>;
    onClaimUpdate?: (id: string, claimId: string | null) => Promise<void>;
    onLinkEntityToMessage?: (id: string, entityType: string, entityIds: string[]) => Promise<void>;
    onUnlinkEntityFromMessage?: (id: string, entityType: string, entityIds: string[]) => Promise<void>;
}

export const MessagingContext = React.createContext<IMessagingContextProps>({
    isReadOnly: true,
    isSubjectEditable: false,
    isListLoading: false,
    isDetailLoading: false,
    enableMessageTypeValidation: false,
    isReadOnlyClaimChoice: false,

    account: undefined,
    message: undefined,
    providerMessage: undefined,
    comments: undefined,
    documents: undefined,
    claims: undefined,

    onClaimSearch: noopClaimSearch,
    onCommentToggle: noop,
    onComposeComment: noop,
    onComposeMessage: noop,
    onDocumentDownload: noop,
    onDocumentAssign: noop,
    onMarkAsClosed: noop,
    onMarkAsOpen: noop,
    onMarkAsRead: noop,
    onMarkAsUnread: noop,
    onMessageClose: noop,
    onMessageOpen: noop,
    onSubjectUpdate: noop,
    onClaimUpdate: noop,
});

export const MessagingProvider: React.FunctionComponent<IMessageProviderProps> = React.memo<IMessageProviderProps>(
    props => {
        const {
            children,
            getCategoryName,
            getUserName,
            onClaimSearch,
            onCommentToggle,
            onComposeComment,
            onComposeMessage,
            onDocumentDownload,
            onDocumentActualDownload,
            onDocumentAssign,
            onDocumentEdit,
            onMarkAsClosed,
            onMarkAsOpen,
            onMarkAsRead,
            onMarkAsUnread,
            onProviderMarkAsClosed,
            onProviderMarkAsOpen,
            onMessageClose,
            onMessageOpen,
            onSubjectUpdate,
            onClaimUpdate,
            onLinkEntityToMessage,
            onUnlinkEntityFromMessage,
            ...providerProps
        } = props;

        // NB: Provider methods are optional, but context methods are not.
        const context = {
            getCategoryName,
            getUserName,
            onClaimSearch: onClaimSearch || noopClaimSearch,
            onCommentToggle: onCommentToggle || noop,
            onComposeComment: onComposeComment || noop,
            onComposeMessage: onComposeMessage || noop,
            onDocumentDownload: onDocumentDownload || noop,
            onDocumentActualDownload: onDocumentActualDownload || undefined,
            onDocumentAssign: onDocumentAssign || noop,
            onDocumentEdit: onDocumentEdit || noop,
            onMarkAsClosed: onMarkAsClosed || noop,
            onMarkAsOpen: onMarkAsOpen || noop,
            onMarkAsRead: onMarkAsRead || noop,
            onMarkAsUnread: onMarkAsUnread || noop,
            onProviderMarkAsClosed: onProviderMarkAsClosed || undefined,
            onProviderMarkAsOpen: onProviderMarkAsOpen || undefined,
            onMessageClose: onMessageClose || noop,
            onMessageOpen: onMessageOpen || noop,
            onSubjectUpdate: onSubjectUpdate || noop,
            onClaimUpdate: onClaimUpdate || noop,
            onLinkEntityToMessage: onLinkEntityToMessage || noop,
            onUnlinkEntityFromMessage: onUnlinkEntityFromMessage || noop,
            ...providerProps,
        };
        return <MessagingContext.Provider value={context}>{children}</MessagingContext.Provider>;
    },
);
